import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { AzureDataExplorerLinkedService, CredentialTypeOptions, Credential, IModel, ReferenceAdapter, adxAuthTypes } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ValueAdapter } from '../value.adapter';
import { azureDefaultAuthRefTypes } from 'app/registries/utls/authenticationUtils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDataExplorerLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                endpoint: '*',
                tenant: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                encryptedCredential: '*',
                database: '*',
                credential: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureDataExplorerLinkedServiceAdapter extends LinkedServiceAdapter<AzureDataExplorerLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureDataExplorerLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationMethod;
        if (model.credential) {
            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = adxAuthTypes.ServicePrincipalAuthentication;
            } else if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = adxAuthTypes.UserAuth;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = adxAuthTypes.UserAssignedManagedIdentityAuthentication;
            } else {
                finalAuthType = model.authenticationMethod;
            }
        }
        model.authenticationMethod = finalAuthType;
    }

    protected modelConstructor(): new () => AzureDataExplorerLinkedService {
        return AzureDataExplorerLinkedService;
    }

    protected model(): IModel {
        return AzureDataExplorerLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureDataExplorerLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureDataExplorerLinkedService;
        properties.type = Metamodel.modelFor(AzureDataExplorerLinkedService).name;
        const isSPInline: boolean = linkedService.authenticationMethod === adxAuthTypes.ServicePrincipalAuthentication
            && linkedService.authReferenceMethod === azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
        const endpoint: string = this.valueToResource(linkedService, AzureDataExplorerLinkedService.ENDPOINT);
        const tenant: string = isSPInline ? this.valueToResource(linkedService, AzureDataExplorerLinkedService.TENANT) : undefined;
        const servicePrincipalId: string = isSPInline ? this.valueToResource(linkedService, AzureDataExplorerLinkedService.SERVICE_PRINCIPAL_ID) : undefined;
        const servicePrincipalKey: string | Model.SecretBase = isSPInline
            ? parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureDataExplorerLinkedService.SERVICE_PRINCIPAL_KEY, this.context)
            : undefined;
        const database: string = this.valueToResource(linkedService, AzureDataExplorerLinkedService.DATABASE);

        properties.typeProperties = {
            endpoint,
            tenant,
            servicePrincipalId,
            servicePrincipalKey,
            database,
            credential: (linkedService.credential
                && ((linkedService.authenticationMethod === adxAuthTypes.ServicePrincipalAuthentication
                    && linkedService.authReferenceMethod === azureDefaultAuthRefTypes.CredentialRefTypeAuthentication)
                    || linkedService.authenticationMethod === adxAuthTypes.UserAssignedManagedIdentityAuthentication
                    || linkedService.authenticationMethod === adxAuthTypes.UserAuth
                )
            ) ? ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureDataExplorerLinkedService): Promise<AzureDataExplorerLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureDataExplorerLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.AzureDataExplorerLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.credential = undefined;

        ValueAdapter.toModel(linkedService, AzureDataExplorerLinkedService.ENDPOINT, typeProperties.endpoint);
        ValueAdapter.toModel(linkedService, AzureDataExplorerLinkedService.DATABASE, typeProperties.database);

        if (typeProperties.tenant && typeProperties.servicePrincipalId) {
            linkedService.authenticationMethod = adxAuthTypes.ServicePrincipalAuthentication;

            ValueAdapter.toModel(linkedService, AzureDataExplorerLinkedService.TENANT, typeProperties.tenant);
            ValueAdapter.toModel(linkedService, AzureDataExplorerLinkedService.SERVICE_PRINCIPAL_ID, typeProperties.servicePrincipalId);
            parameterizableSecretBaseToModel(linkedService, AzureDataExplorerLinkedService.SERVICE_PRINCIPAL_KEY, typeProperties.servicePrincipalKey, this.context);

            linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
        } else {
            // set correct auth method in postToModel call if credential reference is present.
            linkedService.authenticationMethod = adxAuthTypes.MSIAuthentication;

            if (typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), properties.typeProperties.credential, this.context);
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;
            }
        }
        return linkedService;
    }
}
